import "./setupPdf";
import React, { useEffect } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  Container, 
  Box, 
  Typography, 
  AppBar, 
  Toolbar, 
  Button, 
  Paper,
  IconButton, 
  Drawer,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PdfReader from './PdfReader';
import Footer from './components/Footer';
import InstructionsTimeline from './components/InstructionsTimeline';
import HorizontalSteps from './components/HorizontalSteps';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import DUK from './pages/DUK';
import Apie from './pages/Apie';
import { initGA, logPageView } from './utils/analytics';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#27548a',
    },
    secondary: {
      main: '#1976d2',
    },
    text: {
      primary: '#254b64',
      secondary: '#7a848d',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Satoshi", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h2: {
      fontWeight: 700,
      fontFamily: 'Satoshi'
    },
    h4: {
      fontWeight: 600,
      fontFamily: 'Satoshi'
    },
    h5: {
      fontWeight: 600,
      fontFamily: 'Satoshi'
    },
    h6: {
      fontWeight: 600,
      fontFamily: 'Satoshi'
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ open, onClose }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '300px',
          bgcolor: 'background.paper',
          p: 2
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        mb: 2
      }}>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        <ListItem component={Link} to="/" onClick={onClose} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
          <ListItemText primary="Pagrindinis" />
        </ListItem>
        <ListItem component={Link} to="/DUK" onClick={onClose} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
          <ListItemText primary="D.U.K." />
        </ListItem>
        <ListItem component={Link} to="/apie" onClick={onClose} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
          <ListItemText primary="Apie" />
        </ListItem>
      </List>
    </Drawer>
  );
};

// Create a separate component for the app content
function AppContent() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever the location changes
    logPageView(location.pathname);
  }, [location]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Add a flex container that takes full viewport height */}
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header/Navigation */}
        <AppBar position="sticky" color="primary" elevation={1} sx={{ bgcolor: 'white' }}>
          <Toolbar sx={{ justifyContent: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              width: '100%', 
              maxWidth: 'lg', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Link to="/">
                <img 
                  src="/Finsense.png" 
                  alt="Finsense Logo" 
                  style={{ 
                    height: '40px', // Base height
                    width: 'auto',
                    marginRight: '8px'
                  }} 
                />
              </Link>
              
              {/* Desktop Navigation */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                gap: 3 
              }}>
                <Button color="primary" component={Link} to="/">Pagrindinis</Button>
                <Button color="primary" component={Link} to="/DUK">D.U.K.</Button>
                <Button color="primary" component={Link} to="/apie">Apie</Button>
              </Box>

              {/* Mobile Menu Button */}
              <IconButton 
                sx={{ 
                  display: { xs: 'flex', md: 'none' },
                  color: 'primary.main'
                }}
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile Menu Drawer */}
        <MobileMenu 
          open={mobileMenuOpen} 
          onClose={() => setMobileMenuOpen(false)} 
        />

        {/* Main content - will grow to fill available space */}
        <Box sx={{ flex: 1 }}>
          <Routes>
            <Route path="/DUK" element={<DUK />} />
            <Route path="/apie" element={<Apie />} />
            <Route path="/" element={
              <>
                {/* Hero Section */}
                <Box sx={{ py: 8, textAlign: 'center', bgcolor: 'background.default' }}>
                  <Container maxWidth="md">
                    <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                      Jungtinės Karalystės studentų paskolos deklaravimas VMI
                    </Typography>
                    <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4 }}>
                      Susigrąžinkite iki 20% nuo sumokėtų studijų įmokų.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button 
                        variant="outlined" 
                        size="large" 
                        component={Link} 
                        to="/DUK"
                      >
                        Sužinokite daugiau
                      </Button>
                    </Box>
                  </Container>
                </Box>

                <HorizontalSteps 
                  steps={[
                    {
                      number: 1,
                      title: "Atsisiųskite paskolos išrašą"
                    },
                    {
                      number: 2,
                      title: "Konvertuokite"
                    },
                    {
                      number: 3,
                      title: "Įkelkite į VMI"
                    }
                  ]}
                />

                <InstructionsTimeline 
                  title="Kaip gauti paskolos išrašą?"
                  steps={[
                    {
                      number: 1,
                      title: "Prisijungimas",
                      description: <>Prisijunkite prie <a href="https://logon.slc.co.uk/welcome/secured/login?svc=ors&_locale=en_GB_GOVUK" target="_blank" rel="noopener noreferrer">Student Finance paskyros</a></>,
                      isActive: true
                    },
                    {
                      number: 2,
                      title: "Ataskaitų atsisiuntimas",
                      description: <>Prisijungus dešinėje pasirinkite <strong>"View your statements"</strong> ir atsisiųskite <strong>visų metų ataskaitas</strong></>
                    },
                    {
                      number: 3,
                      title: "Ataskaitų įkėlimas",
                      description: <>Įkelkite visas gautas ataskaitas apatiniame lange, pasirinkite mokestinius metus ir atsisiųskite sugeneruotą metinę suvestinę</>
                    }
                  ]}
                  imageSrc="https://finsense-assets.s3.eu-north-1.amazonaws.com/StudFinance.gif"
                  imageAlt="Instrukcija kaip atsisiųsti paskolos išrašą is student finance"
                />

                {/* PDF Reader Section */}
                <Container maxWidth="lg" sx={{ my: 6 }}>
                  <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight="bold">
                      Įkelkite savo finansų ataskaitas
                    </Typography>
                    <PdfReader />
                  </Paper>
                </Container>
                

                <Container maxWidth="lg" sx={{ my: 8 }}>
                  <InstructionsTimeline 
                    title="Kaip užpildyti VMI?"
                    steps={[
                      {
                        number: 1,
                        title: "Prisijungimas ir deklaracijos pradžia",
                        description: <>Prisijunkite prie VMI sistemos ir pradėkite pildyti deklaraciją už pasirinktus metus</>,
                        isActive: true
                      },
                      {
                        number: 2,
                        title: "Išlaidų deklaravimas",
                        description: <>Skiltyje "Išlaidos, mažinančios pajamas" pasirinkite "Taip, patyriau", o kitame žingsnyje – "Ne". Tada pasirinkite: "Grąžintas studijų ar profesinio mokymo kreditas ar jo dalis", o vėliau pažymėkite, kad tai įvyko užsienio valstybėje</>
                      },
                      {
                        number: 3,
                        title: "Dokumentų įkėlimas",
                        description: <>Užpildykite duomenis pagal mūsų sugeneruotą ataskaitą ir įkelkite visus Student Finance išrašus bei mūsų parengtą ataskaitą</>
                      }
                    ]}
                    imageSrc="https://finsense-assets.s3.eu-north-1.amazonaws.com/VMI.gif"
                    imageAlt="VMI instrukcija kaip deklaruoti JK studijų paskolą"
                  />
                </Container>
              </>
            } />
          </Routes>
        </Box>

        {/* Footer will stay at bottom */}
        <Footer />
      </Box>
    </ThemeProvider>
  );
}

// Main App component
function App() {
  useEffect(() => {
    // Initialize GA4 when the app loads
    initGA();
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
